import {
  reactQueryPost,
  reactQueryDelete,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useState, useCallback } from "react";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

interface UseBlockMemberProps {
  memberName?: string;
  onSuccess?: () => void;
}

interface MemberToBlock {
  community_member_id: number;
  id: number;
  name: string;
}

export const useBlockMember = ({
  onSuccess,
  memberName,
}: UseBlockMemberProps = {}) => {
  const [memberToBlock, setMemberToBlock] = useState<MemberToBlock | null>(
    null,
  );
  const [isBlockDmsModalOpen, setIsBlockDmsModalOpen] = useState(false);
  const toastr = useToast();
  const { mutate: blockMember, isPending: isBlocking } = useMutation({
    mutationFn: async ({ memberId }: { memberId: number }) => {
      await reactQueryPost(
        internalApi.blockedMembers.create({
          params: {
            blocked_member_id: memberId,
          },
        }),
      );
    },
    onError: (error: Error) => {
      toastr.error(error.message);
    },
    onSuccess: () => {
      toastr.success(
        t("messaging.block_direct_messages_modal.success", {
          name: memberToBlock?.name || memberName,
        }),
      );
      setIsBlockDmsModalOpen(false);
      onSuccess?.();
    },
  });

  interface UnBlockMemberProps {
    memberId: number;
    name?: string;
  }

  const { mutate: unBlockMember, isPending: isUnblocking } = useMutation({
    mutationFn: async ({ memberId }: UnBlockMemberProps) => {
      await reactQueryDelete(
        internalApi.blockedMembers.destroy({
          id: memberId,
        }),
      );
    },
    onError: (error: Error) => {
      toastr.error(error.message);
    },
    onSuccess: (_, { name }) => {
      toastr.success(
        t("messaging.unblock_direct_messages_success", {
          name: name || memberName,
        }),
      );
      onSuccess?.();
    },
  });

  const toggleBlockDmsModal = useCallback(
    (member: MemberToBlock) => {
      setMemberToBlock(member);
      setIsBlockDmsModalOpen(true);
    },
    [setMemberToBlock, setIsBlockDmsModalOpen],
  );

  const handleBlockMember = useCallback(() => {
    if (memberToBlock) {
      blockMember({ memberId: memberToBlock.community_member_id });
    }
  }, [memberToBlock, blockMember]);

  const handleUnblockMember = useCallback(
    (member: any) => {
      if (!isUnblocking) {
        unBlockMember({
          memberId: member?.community_member_id,
          name: member?.name,
        });
      }
    },
    [isUnblocking, unBlockMember],
  );

  return {
    blockMember,
    isBlocking,
    memberToBlock,
    setMemberToBlock,
    unBlockMember,
    isUnblocking,
    toggleBlockDmsModal,
    handleBlockMember,
    handleUnblockMember,
    isBlockDmsModalOpen,
    setIsBlockDmsModalOpen,
  };
};
